import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { globalStyles } from '../styles/globalStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getPlanById, PLAN_IDS, formatCentsAsDollars } from '../constants/subscriptionPlans';

export function SubscriptionSuccess({ navigation }) {
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [planId, setPlanId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const verifySubscription = async () => {
      try {
        const auth = getAuth();
        const userId = auth.currentUser?.uid;
        if (!userId) {
          if (!cancelled) {
            setError('Please sign in to verify your subscription.');
            setIsVerifying(false);
          }
          return;
        }

        const db = getFirestore();
        const userRef = doc(db, 'users', userId);

        const poll = async () => {
          attempts += 1;
          const userDoc = await getDoc(userRef);
          if (!userDoc.exists()) {
            if (attempts < 6) {
              setTimeout(poll, 1500);
              return;
            }
            if (!cancelled) {
              setError('Something went wrong. Please try again.');
              setIsVerifying(false);
            }
            return;
          }

          const userData = userDoc.data() || {};
          if (userData.subscriptionStatus === 'active') {
            if (!cancelled) {
              setPlanId(userData.subscriptionPlan || null);
              setIsSubscribed(true);
              setIsVerifying(false);
            }
            return;
          }

          if (attempts < 6) {
            setTimeout(poll, 1500);
            return;
          }

          if (!cancelled) {
            setError('Subscription is still processing. If you were charged, it will activate shortly.');
            setIsVerifying(false);
            setTimeout(() => navigation.replace('Plans'), 5000);
          }
        };

        await poll();
      } catch (err) {
        console.error('Error verifying subscription status:', err);
        if (!cancelled) {
          setError('Something went wrong verifying your subscription.');
          setIsVerifying(false);
          setTimeout(() => navigation.replace('Plans'), 5000);
        }
      }
    };

    verifySubscription();
    return () => {
      cancelled = true;
    };
  }, [navigation]);

  const handleContinue = () => {
    navigation.navigate('Home');
  };

  const plan = getPlanById(planId);
  const hasFullAccess = plan?.includesMap;

  if (error) {
    return (
      <View style={globalStyles.container}>
        <BackgroundVideo />
        <View style={[globalStyles.overlay, globalStyles.plansContent]}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorTitle}>Subscription Error</Text>

            <View style={styles.errorCard}>
              <Text style={styles.errorMessage}>{error}</Text>
              <Text style={styles.errorHelp}>
                Don't worry! If you've been charged, your payment is safe. Our team has been notified and will resolve this shortly.
              </Text>
              <View style={styles.supportSection}>
                <Text style={styles.supportTitle}>
                  Need help?{' '}
                  <Text
                    onPress={() => navigation.navigate('Contact')}
                    style={[styles.supportText, { textDecorationLine: 'underline', color: '#0bb5df', fontSize: 16 }]}
                  >
                    Contact us
                  </Text>
                </Text>
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[globalStyles.subscribeButton, styles.retryButton]}
                onPress={() => navigation.navigate('Plans')}
              >
                <Text style={globalStyles.subscribeButtonText}>Return to Plans</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[globalStyles.subscribeButton, styles.homeButton]}
                onPress={() => navigation.navigate('Home')}
              >
                <Text style={[globalStyles.subscribeButtonText, { color: 'white' }]}>Go to Home</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  }

  if (isVerifying && !isSubscribed) {
    return (
      <View style={globalStyles.container}>
        <BackgroundVideo />
        <View style={[globalStyles.overlay, globalStyles.plansContent]}>
          <View style={globalStyles.planCard}>
            <ActivityIndicator size="large" color="#fff" />
            <Text style={[globalStyles.featureText, { textAlign: 'center', marginTop: 20 }]}>
              Verifying your subscription...
            </Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={globalStyles.container}>
      <BackgroundVideo />
      <View style={[globalStyles.overlay, globalStyles.plansContent]}>
        <View style={globalStyles.planCard}>
          <View style={globalStyles.planHeader}>
            <Text style={[globalStyles.planName, { fontSize: 28, marginBottom: 16 }]}>
              You're subscribed
            </Text>
            <Text style={[globalStyles.featureText, { textAlign: 'center', marginBottom: 8 }]}>
              {plan?.name || 'Your plan'} is active
              {plan ? ` at ${formatCentsAsDollars(plan.priceCents)}/mo` : ''}.
            </Text>
          </View>

          <View style={globalStyles.featuresContainer}>
            <Text style={globalStyles.featuresTitle}>You now have access to:</Text>
            {(plan?.features || ['Calendar access']).map((feature) => (
              <View key={feature} style={globalStyles.featureItem}>
                <Text style={globalStyles.featureIcon}>✓</Text>
                <Text style={globalStyles.featureText}>{feature}</Text>
              </View>
            ))}
            {hasFullAccess ? (
              <View style={globalStyles.featureItem}>
                <Text style={globalStyles.featureIcon}>✓</Text>
                <Text style={globalStyles.featureText}>
                  Submit approved locations to earn Credits for your Wallet
                </Text>
              </View>
            ) : null}
          </View>

          <TouchableOpacity style={globalStyles.subscribeButton} onPress={handleContinue}>
            <Text style={globalStyles.subscribeButtonText}>Continue to App</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  errorContainer: {
    width: '100%',
    maxWidth: 600,
    padding: 20,
    alignItems: 'center',
  },
  errorTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 32,
    color: '#e74c3c',
    marginBottom: 20,
    textAlign: 'center',
  },
  errorCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 15,
    padding: 25,
    width: '100%',
    marginBottom: 30,
    borderWidth: 1,
    borderColor: '#e74c3c',
  },
  errorMessage: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    color: '#e74c3c',
    marginBottom: 15,
    textAlign: 'center',
  },
  errorHelp: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 24,
  },
  supportSection: {
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 20,
    marginTop: 10,
  },
  supportTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
    fontWeight: '600',
  },
  supportText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 15,
    width: '100%',
    flexWrap: 'wrap',
  },
  retryButton: {
    backgroundColor: '#ffffff',
  },
  homeButton: {
    backgroundColor: '#4e7de0',
  },
});
